import { useState } from 'react';
import { Plane, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useLive } from '../lib/useLive';

type Ann = { id: string; message: string };

const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * EL AVIÓN CON EL CARTEL.
 *
 * Los anuncios internos se perdían en el email. Esto es lo contrario de una notificación: cruza la
 * pantalla como el avioncito de la playa arrastrando su banderola, en verde-menta —no rojo ni
 * ámbar, que ya significan "algo anda mal"— porque un anuncio de la empresa es una buena noticia,
 * no una alarma.
 *
 * Se cierra con click y no vuelve (a diferencia de la barra del COC, que reaparece: aquella es
 * plata sin cobrar y esta es un aviso ya leído).
 *
 * Con `prefers-reduced-motion` el avión no vuela y el mensaje queda quieto y legible: la gracia no
 * puede costarle a alguien poder leerlo.
 *
 * SE ENTERA SOLO. Antes esto consultaba una vez al montar y nadie recarga una SPA: se publicaba un
 * anuncio y no lo veía nadie hasta que por casualidad apretaban F5 — podía pasar el día entero.
 * Ahora entra por intervalo, al volver a la pestaña y al cambiar de pantalla (ver `useLive`).
 */
export function BroadcastBanner() {
  const { t } = useI18n();
  const { data } = useLive(() => api.get<{ announcement: Ann | null }>('/api/broadcast/in-app'), { everyMs: 45_000 });
  // Cerrado a mano: se apaga al toque y NO vuelve a aparecer aunque el poll lo siga trayendo
  // (el backend recién lo deja de mandar cuando registra el "visto").
  const [cerrados, setCerrados] = useState<string[]>([]);
  const a = data?.announcement && !cerrados.includes(data.announcement.id) ? data.announcement : null;
  if (!a) return null;

  const cerrar = () => {
    const id = a.id;
    setCerrados((p) => [...p, id]);
    api.post(`/api/broadcast/in-app/${id}/seen`, {}).catch(() => {});
  };

  const quieto = reduceMotion();

  return (
    <div className="bc-banner" role="status">
      <div className={`bc-sky${quieto ? ' bc-still' : ''}`}>
        <div className="bc-plane">
          <span className="bc-plane-ico" aria-hidden><Plane size={16} strokeWidth={1.75} /></span>
          {/* La banderola: el avión tira de ella, así que va DESPUÉS del avión y con la cuerdita. */}
          <span className="bc-rope" aria-hidden />
          <span className="bc-flag">{a.message}</span>
        </div>
      </div>
      <button className="bc-banner-x" onClick={cerrar} title={t('bcast.gotIt')} aria-label={t('bcast.gotIt')}><X size={14} strokeWidth={1.75} /></button>
    </div>
  );
}
